import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Form, Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiDownload, BiPlus, BiUpload } from 'react-icons/bi';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoaderContext } from '../../../App';
import { LS_AUTHORITY_KEY } from '../../../core/constants/common';
import useHasPermission from '../../../core/hooks/useHasPermission';
import GHGEmissionMasterDataService from '../../../core/service/GHGEmissionMasterDataService';
import GHGEmissionFactors from './GHGEmissionFactors';
import GHGEmissionSources from './GHGEmissionSources';
import './GHGEmissionDatasetContainer.scss';

const GHGEmissionDatasetContainer = ({ t }) => {
	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const { setLoaderDisplay } = useContext(LoaderContext);
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
 const {isSuperAdmin}  = useHasPermission();

  const getCompanyId = () => {
    if (isSuperAdmin()) {
      return null;
    }
    return lsData?.profile?.company?.id || null;
  };

  const companyId = getCompanyId();
  const isCompanyAdmin = companyId > 0;

  const [activeTab, setActiveTab] = useState('sources');
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState('');
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [searchKey, setSearchKey] = useState('');
  const [selectedSource, setSelectedSource] = useState(null);

  // Sources state
  const [showAddSourceModal, setShowAddSourceModal] = useState(false);
  const [showAddFactorModal, setShowAddFactorModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = () => {
    setLoaderDisplay(true);
    GHGEmissionMasterDataService.listDatasets()
      .then((response) => {
        if (response.status === 200) {
          const datasetsList = response.data || [];
          setDatasets(datasetsList);
          if (datasetsList.length > 0) {
            const firstDataset = datasetsList[0].name || datasetsList[0];
            setSelectedDataset(firstDataset);
            loadVersions(firstDataset);
          }
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  const handleDatasetChange = (datasetName) => {
    setSelectedDataset(datasetName);
    setSearchKey('');
    setSelectedSource(null);
    setSelectedVersion('');
    setVersions([]);
    if (datasetName) {
      loadVersions(datasetName);
    }
  };

  const loadVersions = (datasetName) => {
    if (!datasetName) return;

    setLoaderDisplay(true);
    GHGEmissionMasterDataService.listVersions(datasetName, companyId)
      .then((response) => {
        if (response.status === 200) {
          // API returns array of version objects
          const versionsList = Array.isArray(response.data) ? response.data : [response.data].filter(Boolean);
          setVersions(versionsList);
          if (versionsList.length > 0) {
            // Use versionNumber for API calls, but show versionName in UI
            const firstVersion = versionsList[0].versionNumber || versionsList[0].versionName;
            setSelectedVersion(firstVersion);
          }
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  const handleVersionChange = (version) => {
    setSelectedVersion(version);
    setSelectedSource(null);
  };


  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'factors' && selectedSource) {
      // Keep the source filter when switching to factors tab
    } else if (key === 'sources') {
      // Clear source filter when switching to sources tab
      setSelectedSource(null);
    }
  };

  const handleSearchChange = (e) => {
    setSearchKey(e.target.value);
  };

  const handleViewFactors = (source) => {
    setSelectedSource(source);
    setActiveTab('factors');
  };

  const handleClearSourceFilter = () => {
    setSelectedSource(null);
  };

  const getHeaderTitle = () => {
    if (activeTab === 'factors' && selectedSource) {
      return `${t("GHGEmissionFactors.title.label")} - ${selectedSource.name}`;
    }
    return activeTab === 'sources'
      ? t("GHGEmissionSources.title.label")
      : t("GHGEmissionFactors.title.label");
  };

  const getAddButtonText = () => {
    return activeTab === 'sources'
      ? t("GHGEmissionSources.addSource.label")
      : t("GHGEmissionFactors.addFactor.label");
  };

  const handleAddClick = () => {
    if (activeTab === 'sources') {
      setShowAddSourceModal(true);
    } else {
      setShowAddFactorModal(true);
    }
  };

  const handleExport = () => {
    if (!selectedDataset || !selectedVersion) return;
    
    setLoaderDisplay(true);
    GHGEmissionMasterDataService.exportVersion(selectedDataset, selectedVersion, companyId)
      .then((response) => {
        setLoaderDisplay(false);
        // Extract filename from Content-Disposition header or generate one
        const filename = `${selectedDataset}_v${selectedVersion}.xlsx`;
        const blob = new Blob([response.data], { type: 'application/octet-stream' });
        const fileURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = filename;
        link.click();
        link.remove();
        window.URL.revokeObjectURL(fileURL);
      })
      .catch((error) => {
        setLoaderDisplay(false);
      });
  };

  const handleImportClick = () => {
    if (!selectedDataset || !selectedVersion) return;
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['.xlsx', '.xls'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!validTypes.includes(fileExtension)) {
      toast.error(t("GHGDatasetSelection.import.invalidFileType.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
      fileInputRef.current.value = null;
      return;
    }

    // Validate file size (e.g., 10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error(t("GHGDatasetSelection.import.fileTooLarge.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
      fileInputRef.current.value = null;
      return;
    }

    importFile(file);
  };

  const importFile = (file) => {
    if (!selectedDataset || !selectedVersion) return;

    const formData = new FormData();
    formData.append('file', file);

    setLoaderDisplay(true);
    GHGEmissionMasterDataService.importVersion(selectedDataset, selectedVersion, formData, companyId)
      .then((response) => {
        setLoaderDisplay(false);
        fileInputRef.current.value = null;
        
        if (response.status === 200 || response.status === 201) {
          const responseData = response.data;
          toast.success(
            t("GHGDatasetSelection.import.success.label", {
              sources: responseData.importedSourcesCount || 0,
              factors: responseData.importedFactorsCount || 0,
            }),
            {
              position: toast.POSITION.TOP_RIGHT,
            }
          );
          // Refresh the data
          setRefreshKey(prev => prev + 1);
        }
      })
      .catch((error) => {
        setLoaderDisplay(false);
        fileInputRef.current.value = null;
      });
  };

  return (
    <div className="ghg-dataset-selection" data-testid="ghg-dataset-selection">
      {/* Header - Left aligned */}
      <div className="mb-3">
        <h5 className="MH mb-0 text-start">{getHeaderTitle()}</h5>
      </div>

      {/* Controls Row - Search left, Dataset & Button right */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          {activeTab === 'sources' && (
            <Form.Control
              type="text"
              placeholder={t("GHGEmissionSources.filters.name.placeholder.label")}
              value={searchKey}
              onChange={handleSearchChange}
              style={{ width: 'auto', minWidth: '200px' }}
              data-testid="search-input"
            />
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <Form.Select
            value={selectedDataset}
            onChange={(e) => handleDatasetChange(e.target.value)}
            style={{ width: 'auto', maxWidth: '200px' }}
            className="d-inline-block"
            data-testid="dataset-selector"
          >
            <option value="">{t("GHGDatasetSelection.selectDataset.label")}</option>
            {datasets.map((dataset) => (
              <option key={dataset.name || dataset} value={dataset.name || dataset}>
                {dataset.name || dataset}
              </option>
            ))}
          </Form.Select>
          {selectedDataset && (
            <>
              <Form.Select
                value={selectedVersion}
                onChange={(e) => handleVersionChange(e.target.value)}
                style={{ width: 'auto', maxWidth: '200px' }}
                className="d-inline-block"
                data-testid="version-selector"
              >
                <option value="">{t("GHGDatasetSelection.selectVersion.label")}</option>
                {versions.map((version) => {
                  const versionValue = version.versionNumber || version.versionName;
                  const displayName = version.versionName || `Version ${version.versionNumber}`;
                  return (
                    <option key={version.id} value={versionValue}>
                      {displayName}
                    </option>
                  );
                })}
              </Form.Select>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  const basePath = isCompanyAdmin ? '/company_admin/ghg' : '/admin';
                  const routePath = isCompanyAdmin 
                    ? `${basePath}/datasets/${selectedDataset}/versions`
                    : `${basePath}/ghg_datasets/${selectedDataset}/versions`;
                  navigate(routePath);
                }}
                data-testid="manage-versions-button"
              >
                {t("GHGDatasetSelection.manageVersions.label")}
              </Button>
            </>
          )}
          {activeTab === 'sources' && (
            <>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>{t("GHGDatasetSelection.export.label")}</Tooltip>}
              >
                <Button
                  variant="outline-primary"
                  onClick={handleExport}
                  data-testid="export-button"
                  disabled={!selectedDataset || !selectedVersion}
                >
                  <BiDownload />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>{t("GHGDatasetSelection.import.label")}</Tooltip>}
              >
                <Button
                  variant="outline-primary"
                  onClick={handleImportClick}
                  data-testid="import-button"
                  disabled={!selectedDataset || !selectedVersion}
                >
                  <BiUpload />
                </Button>
              </OverlayTrigger>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                data-testid="file-input"
              />
            </>
          )}
          <Button
            variant="primary"
            onClick={handleAddClick}
            data-testid="add-button"
            disabled={!selectedDataset || !selectedVersion}
          >
            <BiPlus className="me-1" />
            {getAddButtonText()}
          </Button>
        </div>
      </div>

      {selectedDataset && (
        <>
          <Nav variant="tabs" className="ghg-dataset-tabs mb-3" activeKey={activeTab}>
            <Nav.Item>
              <Nav.Link
                eventKey="sources"
                onClick={() => handleTabChange('sources')}
                data-testid="sources-tab"
              >
                {t("GHGDatasetSelection.tabs.sources.label")}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="factors"
                onClick={() => handleTabChange('factors')}
                data-testid="factors-tab"
              >
                {t("GHGDatasetSelection.tabs.factors.label")}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {activeTab === 'sources' && selectedVersion && (
            <GHGEmissionSources
              key={refreshKey}
              datasetName={selectedDataset}
              version={selectedVersion}
              searchKey={searchKey}
              onViewFactors={handleViewFactors}
              showAddModal={showAddSourceModal}
              onHideAddModal={() => setShowAddSourceModal(false)}
              companyId={companyId}
            />
          )}

          {activeTab === 'factors' && selectedVersion && (
            <GHGEmissionFactors
              key={refreshKey}
              datasetName={selectedDataset}
              version={selectedVersion}
              selectedSource={selectedSource}
              onClearSourceFilter={handleClearSourceFilter}
              onAddFactor={showAddFactorModal}
              onHideAddFactor={() => setShowAddFactorModal(false)}
              companyId={companyId}
            />
          )}
        </>
      )}


      {!selectedDataset && datasets.length === 0 && (
        <div className="text-center p-4">
          <p>{t("GHGDatasetSelection.noDatasets.label")}</p>
        </div>
      )}
    </div>
  );
};

export default withTranslation()(GHGEmissionDatasetContainer);
